import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import {
  AdminChatPropertiesType,
  AdminChatPropertyDetailType,
  AdminChatPropertyOperationalNoteType,
  PropertyCharacteristicType,
  SellerPropertyCharacteristicResponse,
} from '@type-def_iqfu/AdminPropertiesType';
import { ChatPropertyDetail } from '@type-def_iqfu/chat/ChatPropertyDetail';
import { ChatPropertyListItem } from '@type-def_iqfu/chat/ChatPropertyListItem';
import { CompanyChatPropertyDetail } from '@type-def_iqfu/chat/CompanyChatPropertyDetail';

import { ChatPropertyDiscardReason, PullDownChatPropertyType, PullDownChatRoomStatusKeys } from '@constants_iqfu';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

const queryString = require('query-string');

export const useChatProperty = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const createSellerChatProperty = async (submitData: any) => {
    const url = `${backendBasePath}/seller/chat_properties`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: submitData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminChatProperty = async (submitData: any) => {
    const url = `${backendBasePath}/admin/chat_properties`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: submitData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAdminChatProperty = async (submitData: any, propertyId: number) => {
    const url = `${backendBasePath}/admin/chat_properties/${propertyId}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: submitData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellerChatPropertiesList = async (): Promise<{ chat_properties: ChatPropertyListItem[] }> => {
    const url = `${backendBasePath}/seller/chat_properties`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellerChatPropertiesDetail = async (chat_property_id: string): Promise<ChatPropertyDetail | void> => {
    const url = `${backendBasePath}/seller/chat_properties/${chat_property_id}`;
    //呼び出せる前にStringになっているからundefined弾くにはこうするしかない
    //string[] | string を stringに変換するためにそうなっている
    //余裕できたらリファクタリングする
    if (chat_property_id === 'undefined') return;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminChatPropertiesDetail = async (chat_property_id: string): Promise<ChatPropertyDetail> => {
    const url = `${backendBasePath}/admin/chat_properties/${chat_property_id}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const addCompanyToChatPropertyBySeller = async (propertyId: number, requestParams: any) => {
    const url = `${backendBasePath}/seller/chat_properties/${propertyId}/add_company`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestParams),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const addCompanyToChatPropertyByAdmin = async (propertyId: number, requestParams: any) => {
    const url = `${backendBasePath}/admin/chat_properties/${propertyId}/add_company`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestParams),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminChatProperties = async (queryParams: {
    offset: number;
    limit: number;
    property_type: keyof typeof PullDownChatPropertyType;
    chat_status: PullDownChatRoomStatusKeys;
    seller_name: string;
    address: string;
    latest_selling_status: PullDownChatRoomStatusKeys;
    has_selling_urls: '' | boolean;
    next_contact_date_from: null | string;
    next_contact_date_to: null | string;
    created_date_from: null | string;
    created_date_to: null | string;
    labels: string;
  }): Promise<AdminChatPropertiesType> => {
    if (queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_properties?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerProperties = async (user_id: string): Promise<AdminChatPropertiesType> => {
    const url = `${backendBasePath}/admin/chat_properties?seller_user_id=${user_id}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminChatPropertyDetail = async (
    chat_property_id: string,
  ): Promise<AdminChatPropertyDetailType | undefined> => {
    if (chat_property_id === 'undefined') return;
    const url = `${backendBasePath}/admin/chat_properties/${chat_property_id}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getPropertyCharacteristics = async (): Promise<{
    property_characteristics: PropertyCharacteristicType[];
  }> => {
    const url = `${backendBasePath}/property_characteristics`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
      });
    return result;
  };

  const getSellerPropertyCharacteristic = async (
    chatPropertyId: string,
  ): Promise<SellerPropertyCharacteristicResponse | undefined> => {
    if (chatPropertyId === 'undefined') return;
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/seller_property_characteristic`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
      });
    return result;
  };

  const updateSellerPropertyCharacteristic = async (chatPropertyId: string, propertyCharacteristicIds: number[]) => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/seller_property_characteristic`;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ property_characteristic_ids: propertyCharacteristicIds }),
    })
      .then((response) => response.json())
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
      });
    return result;
  };

  const getAdminChatPropertyOperationalNote = async (
    chat_property_id: string,
  ): Promise<AdminChatPropertyOperationalNoteType | undefined> => {
    if (chat_property_id === 'undefined') return;
    const url = `${backendBasePath}/admin/chat_properties/${chat_property_id}/operational_note`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAdminChatPropertyOperationalNote = async (
    chat_property_id: string,
    submitData: AdminChatPropertyOperationalNoteType,
  ) => {
    const url = `${backendBasePath}/admin/chat_properties/${chat_property_id}/operational_note`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyChatPropertyDetail = async (
    chat_room_id: string,
    chat_property_id: string,
  ): Promise<CompanyChatPropertyDetail> => {
    const url = `${backendBasePath}/company/chat_rooms/${chat_room_id}/chat_properties/${chat_property_id}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteChatProperty = async (chatPropertyId: number, discardReason: keyof typeof ChatPropertyDiscardReason) => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}?discard_reason=${discardReason}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteChatRoom = async (chatRoomId: string) => {
    const url = `${backendBasePath}/admin/chat_rooms/${chatRoomId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const sendUpdateNotification = async (
    chatPropertyId: string,
    companyIds: number[],
  ): Promise<{ success?: boolean; error?: string }> => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/send_update_notification`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ company_ids: companyIds }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminChatPropertyExteriorPhoto = async (
    chatPropertyId: string | number,
    formData: FormData,
  ): Promise<{ ok: boolean }> => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/exterior_photos`;
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => ({ ok: response.ok }))
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { ok: false };
      });
  };

  const deleteAdminChatPropertyExteriorPhoto = async (
    chatPropertyId: string | number,
    photoId: number,
  ): Promise<{ ok: boolean }> => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/exterior_photos/${photoId}`;
    return fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => ({ ok: response.ok }))
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { ok: false };
      });
  };

  const previewAdminChatPropertyExteriorPhoto = async (
    chatPropertyId: string | number,
    photoId: number,
  ): Promise<string | undefined> => {
    const url = `${backendBasePath}/admin/chat_properties/${chatPropertyId}/exterior_photos/${photoId}/download`;
    return fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (!response.ok) return undefined;
        return response.blob().then((blob) => URL.createObjectURL(blob));
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return undefined;
      });
  };

  return {
    createSellerChatProperty,
    createAdminChatProperty,
    updateAdminChatProperty,
    getSellerChatPropertiesList,
    getSellerChatPropertiesDetail,
    getAdminChatPropertiesDetail,
    getPropertyCharacteristics,
    getSellerPropertyCharacteristic,
    updateSellerPropertyCharacteristic,
    getAdminChatPropertyOperationalNote,
    updateAdminChatPropertyOperationalNote,
    addCompanyToChatPropertyBySeller,
    addCompanyToChatPropertyByAdmin,
    getAdminChatProperties,
    getAdminChatPropertyDetail,
    getAdminSellerProperties,
    getCompanyChatPropertyDetail,
    deleteChatProperty,
    deleteChatRoom,
    sendUpdateNotification,
    createAdminChatPropertyExteriorPhoto,
    deleteAdminChatPropertyExteriorPhoto,
    previewAdminChatPropertyExteriorPhoto,
  };
};
